import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CalendarCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOTS = [
  { id: "16:00", label: "4:00 – 5:00 PM", startMin: 16 * 60 },
  { id: "17:00", label: "5:00 – 6:00 PM", startMin: 17 * 60 },
  { id: "20:30", label: "8:30 – 9:30 PM", startMin: 20 * 60 + 30 },
];

const buildDays = () => {
  const days = [];
  const now = new Date();
  for (let i = 0; days.length < 12 && i < 20; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    if (d.getDay() === 0) continue; // no Sundays
    days.push({
      iso: d.toLocaleDateString("en-CA"),
      dow: d.toLocaleDateString("en-GB", { weekday: "short" }),
      label: d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
      isToday: i === 0,
    });
  }
  return days;
};

const istMinutesNow = () => {
  const ist = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return ist.getHours() * 60 + ist.getMinutes();
};

export const Scheduler = ({ profile, onBooked }) => {
  const days = useMemo(buildDays, []);
  const [date, setDate] = useState(days[0].iso);
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoadingSlots(true);
      setSlot(null);
      try {
        const res = await axios.get(`${API}/slots`, { params: { date } });
        setSlots(res.data.slots);
      } catch {
        toast.error("Could not load availability. Please try again.");
      } finally {
        setLoadingSlots(false);
      }
    };
    load();
  }, [date]);

  const available = (id) => {
    const s = slots?.find((x) => x.id === id);
    if (!s || !s.available) return false;
    const day = days.find((d) => d.iso === date);
    if (day?.isToday) {
      const def = SLOTS.find((x) => x.id === id);
      if (def.startMin <= istMinutesNow()) return false;
    }
    return true;
  };

  const confirm = async () => {
    if (!slot) return;
    setBooking(true);
    try {
      const res = await axios.post(`${API}/appointments`, {
        profile_ref: profile.ref_id,
        full_name: profile.full_name,
        mobile: profile.mobile,
        email: profile.email || null,
        date,
        slot,
      });
      onBooked(res.data);
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error("That slot was just booked. Please choose another.");
        const res = await axios.get(`${API}/slots`, { params: { date } });
        setSlots(res.data.slots);
        setSlot(null);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setBooking(false);
    }
  };

  return (
    <div className="mt-12 text-left border border-line bg-white p-7 sm:p-10" data-testid="scheduler">
      <div className="flex items-center gap-3">
        <CalendarCheck size={20} className="text-gold" strokeWidth={1.5} />
        <h2 className="font-serif text-2xl text-forest">Choose your consultation time</h2>
      </div>
      <p className="mt-3 text-sm text-inkmuted leading-relaxed">
        Weekday slots, India Standard Time. No consultations on Sundays. Your request will be confirmed by our
        team.
      </p>

      <p className="mt-8 font-mono text-[11px] tracking-[0.2em] uppercase text-inkmuted">Select a day</p>
      <div className="mt-3 flex gap-2 overflow-x-auto pb-2" data-testid="schedule-date-strip">
        {days.map((d) => (
          <button
            key={d.iso}
            type="button"
            data-testid={`date-chip-${d.iso}`}
            onClick={() => setDate(d.iso)}
            aria-pressed={date === d.iso}
            className={`shrink-0 px-4 py-3 border text-center transition-colors duration-300 ${
              date === d.iso ? "bg-forest text-ivory border-forest" : "bg-white text-ink border-line hover:border-gold/60"
            }`}
          >
            <span className="block font-mono text-[10px] tracking-[0.18em] uppercase opacity-70">{d.dow}</span>
            <span className="block text-sm font-semibold mt-0.5">{d.label}</span>
          </button>
        ))}
      </div>

      <p className="mt-8 font-mono text-[11px] tracking-[0.2em] uppercase text-inkmuted">Select a time (IST)</p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="schedule-slot-grid">
        {SLOTS.map((s) => {
          const ok = !loadingSlots && available(s.id);
          return (
            <button
              key={s.id}
              type="button"
              data-testid={`slot-${s.id.replace(":", "")}`}
              disabled={!ok}
              onClick={() => setSlot(s.id)}
              aria-pressed={slot === s.id}
              className={`px-4 py-4 border text-sm font-semibold tracking-wide transition-colors duration-300 ${
                slot === s.id
                  ? "bg-forest text-ivory border-forest"
                  : ok
                    ? "bg-white text-ink border-line hover:border-gold/60"
                    : "bg-paper text-inkmuted/40 border-line cursor-not-allowed line-through"
              }`}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        data-testid="confirm-booking-btn"
        onClick={confirm}
        disabled={!slot || booking}
        className="mt-8 w-full sm:w-auto inline-flex justify-center items-center rounded-full bg-forest text-ivory px-9 py-4 text-sm font-semibold tracking-wide border border-gold/40 hover:bg-forestdeep transition-colors duration-300 disabled:opacity-50"
      >
        {booking ? "Confirming…" : "Confirm Consultation Request"}
      </button>
    </div>
  );
};
